import { AnimatedContentLoader, Button, Empty, Prompt } from '@components'
import { useBusinessContext } from '@context'
import { getWorkflowBuilderPageUrl } from '@constants'
import {
  createWorkflow,
  deleteWorkflow,
  IWorkflow,
  queryKeyForWorkflow,
  queryKeyForWorkflows,
  updateWorkflow,
  useWorkflows
} from '@query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { EMPTY_FLOW } from '../list/emptyFlow'
import { WorkflowCreateCard } from '../list/WorkflowCreateCard'
import { WorkflowCard } from '../list/WorkflowCard'
import { WorkflowsEmptyState } from '../list/WorkflowsEmptyState'
import { toWorkflowOrdering, WorkflowSortState } from '../list/workflowSortUtils'

interface Props {
  sort: WorkflowSortState
}

export const WorkflowsTab: React.FC<Props> = ({ sort }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const history = useHistory()
  const queryClient = useQueryClient()

  const [workflowToDelete, setWorkflowToDelete] = useState<IWorkflow | null>(null)

  const ordering = toWorkflowOrdering(sort)
  const { data, isLoading, isError, refetch, isFetching } = useWorkflows(ordering)
  const workflows = data?.results || []

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeyForWorkflows(slug) })

  const createMutation = useMutation({
    mutationFn: () =>
      createWorkflow({ slug }, { name: t('workflows.untitledName'), react_flow_data: EMPTY_FLOW }),
    onSuccess: async workflow => {
      await invalidate()
      history.push(getWorkflowBuilderPageUrl(slug, workflow.id))
    },
    onError: () => {
      toast.error(t('workflows.toasts.createFailed'))
    }
  })

  const toggleMutation = useMutation({
    mutationFn: ({ workflow, enabled }: { workflow: IWorkflow; enabled: boolean }) =>
      updateWorkflow({ slug, workflowId: workflow.id }, { enabled }),
    onSuccess: async updated => {
      await invalidate()
      toast.success(
        updated.enabled ? t('workflows.toasts.enabled') : t('workflows.toasts.disabled')
      )
    },
    onError: () => {
      toast.error(t('workflows.toasts.updateFailed'))
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (workflow: IWorkflow) => deleteWorkflow({ slug, workflowId: workflow.id }),
    onSuccess: async (_, workflow) => {
      queryClient.removeQueries({ queryKey: queryKeyForWorkflow(slug, workflow.id) })
      await invalidate()
      setWorkflowToDelete(null)
      toast.success(t('workflows.toasts.deleted'))
    },
    onError: () => {
      toast.error(t('workflows.toasts.deleteFailed'))
    }
  })

  const handleCreate = () => {
    if (!createMutation.isPending) {
      createMutation.mutate()
    }
  }

  return (
    <>
      <AnimatedContentLoader
        isLoading={isLoading}
        isEmpty={workflows.length === 0}
        isEmptyContent={
          <EmptyCard>
            <WorkflowsEmptyState onCreate={handleCreate} isCreating={createMutation.isPending} />
          </EmptyCard>
        }
        isError={isError}
        errorDescription={
          <Empty>
            <ErrorState>
              <p>{t('workflows.loadError')}</p>
              <Button type="button" size="sm" onClick={() => refetch()} showSpinner={isFetching}>
                {t('general.retry')}
              </Button>
            </ErrorState>
          </Empty>
        }
      >
        <ListLayout>
          <CardGrid>
            <WorkflowCreateCard onCreate={handleCreate} isCreating={createMutation.isPending} />
            {workflows.map(workflow => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                sortField={sort.field}
                onOpen={() => history.push(getWorkflowBuilderPageUrl(slug, workflow.id))}
                onToggleEnabled={enabled => toggleMutation.mutate({ workflow, enabled })}
                onDelete={() => setWorkflowToDelete(workflow)}
              />
            ))}
          </CardGrid>
        </ListLayout>
      </AnimatedContentLoader>

      <Prompt
        title={t('workflows.deleteConfirm.title')}
        description={t('workflows.deleteConfirm.description', {
          name: workflowToDelete?.name || ''
        })}
        isVisible={Boolean(workflowToDelete)}
        onClose={() => setWorkflowToDelete(null)}
        buttons={[
          {
            text: t('workflows.deleteConfirm.cancel'),
            action: () => setWorkflowToDelete(null)
          },
          {
            text: t('workflows.deleteConfirm.confirm'),
            intent: 'danger',
            showSpinner: deleteMutation.isPending,
            action: () => workflowToDelete && deleteMutation.mutate(workflowToDelete)
          }
        ]}
      />
    </>
  )
}

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const EmptyCard = styled.div`
  width: 100%;
  max-width: 960px;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
`

const ListLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  max-width: 960px;
  padding-bottom: ${({ theme }) => theme.spacing.xl}rem;
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.md}rem;
`
